import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { env } from "@/config/env";

const ForgotPasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      const response = await axios.post(`${env.authApiUrl}/forgot-password`, {
        email,
      });

      if (response.data) {
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-main-content bg-white dark:bg-[#0a0e19] py-[60px] md:py-[80px] lg:py-[135px]">
        <div className="mx-auto px-[12.5px] md:max-w-[720px] lg:max-w-[960px] xl:max-w-[1255px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[25px] items-center">
            <div className="xl:ltr:-mr-[25px] xl:rtl:-ml-[25px] 2xl:ltr:-mr-[45px] 2xl:rtl:-ml-[45px] rounded-[25px] order-2 lg:order-1">
              <img
                src="/images/forgot-password.jpg"
                alt="forgot-password-image"
                className="rounded-[25px]"
                width={646}
                height={804}
              />
            </div>

            <div className="xl:ltr:pl-[90px] xl:rtl:pr-[90px] 2xl:ltr:pl-[120px] 2xl:rtl:pr-[120px] order-1 lg:order-2">
              <img
                src="/images/logo-big.svg"
                alt="logo"
                className="inline-block dark:hidden"
                width={142}
                height={38}
              />
              <img
                src="/images/white-logo-big.svg"
                alt="logo"
                className="hidden dark:inline-block"
                width={142}
                height={38}
              />

              <div className="my-[17px] md:my-[25px]">
                <h1 className="!font-semibold !text-[22px] md:!text-xl lg:!text-2xl !mb-[5px] md:!mb-[12px]">
                  {t("auth.forgotPasswordTitle")}
                </h1>
                <p className="font-medium leading-[1.5] lg:text-md text-[#445164] dark:text-gray-400">
                  {t("auth.forgotPasswordDescription")}
                </p>
              </div>

              {error && (
                <div className="mb-[15px] p-[12px] rounded-md bg-danger-50 dark:bg-danger-900 text-danger-600 dark:text-danger-400 border border-danger-200 dark:border-danger-700">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-[15px] p-[12px] rounded-md bg-success-50 dark:bg-success-900 text-success-600 dark:text-success-400 border border-success-200 dark:border-success-700">
                  Password reset email sent! Please check your inbox.
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-[15px] relative">
                  <label
                    htmlFor="email"
                    className="mb-[10px] md:mb-[12px] text-black dark:text-white font-medium block"
                  >
                    {t("common.email")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-[55px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500"
                    placeholder="example@biarthr.com"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || success}
                  className="md:text-md block w-full text-center transition-all rounded-md font-medium mt-[20px] md:mt-[25px] py-[12px] px-[25px] text-white bg-primary-500 hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center justify-center gap-[5px]">
                    <i className="material-symbols-outlined">autorenew</i>
                    {loading ? "Sending..." : success ? "Email Sent!" : t("common.resetPassword")}
                  </span>
                </button>

                <p className="mt-[15px] md:mt-[20px]">
                  Back to{" "}
                  <Link
                    to="/sign-in"
                    className="text-primary-500 transition-all font-semibold hover:underline"
                  >
                    {t("common.signIn")}
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordForm;
